import React from "react";
import { useFormikContext, useField, Field } from "formik";
import { validateExternalIdRequiresArn } from "integrations/aws/utils/awsValidation";
import useProductName from "brand-customization/useProductName";
import { Input } from "components/bootstrap";
import { FormikFormGroup, Select } from "components/common";
import useAWSRegions from "archive/hooks/useAWSRegions";
import { S3_PROVIDER_COPY, EXTERNAL_ID_HELP } from "archive/logic/s3CompatibleProviders";
import { S3_AUTH_MODE } from "./types";
const AUTH_OPTIONS = [
  { value: S3_AUTH_MODE.AUTOMATIC, label: "Automatic" },
  { value: S3_AUTH_MODE.KEY_SECRET, label: "Key & Secret" }
];
export const validateS3CredentialsFields = (credentials) => {
  var _a;
  const errors = {};
  if (!credentials) return errors;
  if (credentials.auth_mode === S3_AUTH_MODE.KEY_SECRET) {
    const requiredMessage = "Required when using Key & Secret authentication.";
    if (!((_a = credentials.aws_access_key_id) == null ? void 0 : _a.trim())) errors.aws_access_key_id = requiredMessage;
    if (!credentials.aws_secret_access_key) errors.aws_secret_access_key = requiredMessage;
  }
  const externalIdError = validateExternalIdRequiresArn(credentials.aws_assume_role_arn, credentials.aws_external_id);
  if (externalIdError) errors.aws_external_id = externalIdError;
  const hasExplicitCredentials = !!credentials.aws_access_key_id || !!credentials.aws_assume_role_arn;
  const hasRegionOrEndpoint = !!credentials.aws_region || !!credentials.endpoint_uri;
  if (hasExplicitCredentials && !hasRegionOrEndpoint) {
    const message = "Neither Region nor Endpoint URI provided";
    errors.aws_region = message;
    errors.endpoint_uri = message;
  }
  return errors;
};
const S3CredentialsFieldGroup = ({ variant = "s3" }) => {
  var _a, _b, _c, _d, _e, _f;
  const { values, touched, setFieldValue, setFieldTouched } = useFormikContext();
  const productName = useProductName();
  const [, { error: secretKeyError, touched: secretKeyTouched }] = useField("credentials.aws_secret_access_key");
  const [, { error: authModeError, touched: authModeTouched }] = useField("credentials.auth_mode");
  const { data: regions } = useAWSRegions();
  const authMode = (_b = (_a = values.credentials) == null ? void 0 : _a.auth_mode) != null ? _b : S3_AUTH_MODE.AUTOMATIC;
  const copy = S3_PROVIDER_COPY[variant];
  const regionEndpointTouched = !!(((_c = touched.credentials) == null ? void 0 : _c.aws_region) || ((_d = touched.credentials) == null ? void 0 : _d.endpoint_uri) || ((_e = touched.credentials) == null ? void 0 : _e.aws_access_key_id) || ((_f = touched.credentials) == null ? void 0 : _f.aws_assume_role_arn));
  const handleAuthModeChange = (nextMode) => {
    if (nextMode === S3_AUTH_MODE.AUTOMATIC) {
      setFieldValue("credentials.aws_access_key_id", void 0);
      setFieldValue("credentials.aws_secret_access_key", void 0);
    }
    setFieldValue("credentials.auth_mode", nextMode);
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "credentials.endpoint_uri",
      type: "text",
      label: copy.endpointLabel,
      help: copy.endpointHelp
    }
  ), /* @__PURE__ */ React.createElement(Field, { name: "credentials.aws_region" }, ({ field: { value: regionValue }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "repository-credentials-aws-region",
      label: copy.regionLabel,
      bsStyle: error && regionEndpointTouched ? "error" : void 0,
      error: regionEndpointTouched ? error : void 0,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      help: copy.regionHelp
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        inputId: "repository-credentials-aws-region-select",
        placeholder: "Automatic",
        "aria-label": copy.regionAriaLabel,
        allowCreate: true,
        options: regions != null ? regions : [],
        onChange: (option) => setFieldValue("credentials.aws_region", option),
        value: regionValue
      }
    )
  )), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "repository-credentials-auth-mode",
      label: copy.authTypeLabel,
      bsStyle: authModeError && authModeTouched ? "error" : void 0,
      error: authModeTouched ? authModeError : void 0,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      help: `Choose how ${productName} authenticates to this bucket.`
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        inputId: "repository-credentials-auth-mode-select",
        placeholder: "Select Authentication Type",
        options: AUTH_OPTIONS,
        onChange: handleAuthModeChange,
        value: authMode
      }
    )
  ), authMode === S3_AUTH_MODE.KEY_SECRET && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "credentials.aws_access_key_id",
      type: "text",
      label: copy.accessKeyLabel,
      placeholder: "AK****************",
      help: copy.accessKeyHelp,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "repository-credentials-secret-key",
      type: "password",
      label: copy.secretKeyLabel,
      error: secretKeyTouched ? secretKeyError : void 0,
      bsStyle: secretKeyError && secretKeyTouched ? "error" : void 0,
      onChange: ({ target: { value } }) => setFieldValue("credentials.aws_secret_access_key", value === "" ? void 0 : { set_value: value }),
      onBlur: () => setFieldTouched("credentials.aws_secret_access_key", true),
      placeholder: "****************",
      help: copy.secretKeyHelp,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      required: true
    }
  )), variant === "s3" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "credentials.aws_assume_role_arn",
      type: "text",
      label: "AWS Assume Role (ARN) (optional)",
      placeholder: "arn:aws:sts::123456789012:assumed-role/some-role",
      help: "Amazon Resource Name with required cross account permission."
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "credentials.aws_external_id",
      type: "text",
      label: "AWS External ID (optional)",
      help: EXTERNAL_ID_HELP
    }
  )));
};
export default S3CredentialsFieldGroup;
